import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Alert as MuiAlert,
  Box, Button,
  CircularProgress,
  Divider,
  FormControl, InputLabel, MenuItem, Paper, Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import { useAuth } from 'contexts/auth'

import { governanceApi } from '../services/governance-api'
import {
  filtersFromSearchParams,
  filtersToSearchParams,
  useGovernanceFilters,
} from '../state/governance-filters'
import {
  AddressabilityFunnel,
  AlertsList,
  CaseDrilldownDialog,
  ExceptionsDrilldownDialog,
  FreshnessBadge,
  AddressabilityLossesDrilldownDialog,
  MaioridadeDrilldownDialog,
  Movements,
  MovementsDrilldownDialog,
  NonOptantsDrilldownDialog,
  PipelineFunnel,
  PolicyOptants,
  Scorecard,
  VigenciasDrilldownDialog,
} from '../components'
import { isGovernanceEnabled } from '../feature-flag'

/**
 * Painel de Governança de Beneficios — P1 (painel-design v2).
 *
 * MVP single-workspace view. Multi-workspace aggregation + grupo econômico
 * cascading filters are deferred to the Phase 5 follow-up (zustand state
 * already supports them; only the UI composition is stubbed here).
 */
export function PainelGovernanca() {
  const auth = useAuth()
  const history = useHistory()
  const location = useLocation()

  const organizationId = auth.user?.organization?.id ?? ''
  const hasSession = !!auth.token

  const filters = useGovernanceFilters()
  const {
    workspaceIds,
    period,
    setWorkspaces,
    setPeriod,
  } = filters

  const workspaceId = workspaceIds[0] ?? ''
  const cpfInputRef = useRef<HTMLInputElement>(null)
  const [selectedCpf, setSelectedCpf] = useState<string | undefined>(undefined)
  const [movementsDrilldownOpen, setMovementsDrilldownOpen] = useState(false)
  const [exceptionsDrilldownOpen, setExceptionsDrilldownOpen] = useState(false)
  const [nonOptantsDrilldownOpen, setNonOptantsDrilldownOpen] = useState(false)
  const [maioridadeDrilldownOpen, setMaioridadeDrilldownOpen] = useState(false)
  const [addressabilityLossesDrilldownCategory, setAddressabilityLossesDrilldownCategory] = useState<'cargo' | 'regiao_lotacao' | null>(null)
  const [vigenciasDrilldownOpen, setVigenciasDrilldownOpen] = useState(false)

  // Hydrate filter state from URL on first render
  useEffect(() => {
    const hydrated = filtersFromSearchParams(location.search)
    if (hydrated.workspaceIds?.length) setWorkspaces(hydrated.workspaceIds)
    if (hydrated.period) setPeriod(hydrated.period)
    // Intentionally runs once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep URL in sync after filter changes
  useEffect(() => {
    const next = filtersToSearchParams(filters)
    if (next !== location.search) history.replace({ pathname: location.pathname, search: next })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceIds, period])

  // 5-minute staleTime matches the painel's soft-SLO window for back-end
  // freshness. Without this, every panel section refetches on every window
  // re-focus, producing flickering loaders and unnecessary load on the
  // aggregation queries.
  const STALE_TIME_MS = 5 * 60 * 1000

  const workspacesQ = useQuery({
    queryKey: ['governance', 'workspaces', organizationId],
    queryFn: () => governanceApi.workspaces(organizationId),
    enabled: hasSession && !!organizationId,
    staleTime: STALE_TIME_MS,
  })

  useEffect(() => {
    // Auto-select first workspace when list loads if none picked yet
    if (!workspaceId && workspacesQ.data?.data.workspaces.length) {
      setWorkspaces([workspacesQ.data.data.workspaces[0].workspace_id])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspacesQ.data])

  const scorecardQ = useQuery({
    queryKey: ['governance', 'scorecard', workspaceId, period.start, period.end],
    queryFn: () => governanceApi.scorecard({ workspaceId, periodStart: period.start, periodEnd: period.end }),
    enabled: !!workspaceId,
    staleTime: STALE_TIME_MS,
  })

  const funnelQ = useQuery({
    queryKey: ['governance', 'pipeline-funnel', workspaceId],
    queryFn: () => governanceApi.pipelineFunnel(workspaceId),
    enabled: !!workspaceId,
    staleTime: STALE_TIME_MS,
  })

  const addressabilityQ = useQuery({
    queryKey: ['governance', 'addressability-funnel', workspaceId, period.start, period.end],
    queryFn: () => governanceApi.addressabilityFunnel({ workspaceId, snapshotStart: period.start, snapshotEnd: period.end }),
    enabled: !!workspaceId,
    staleTime: STALE_TIME_MS,
  })

  const optantsQ = useQuery({
    queryKey: ['governance', 'policy-optants', workspaceId],
    queryFn: () => governanceApi.policyOptants(workspaceId),
    enabled: !!workspaceId,
    staleTime: STALE_TIME_MS,
  })

  const movementsQ = useQuery({
    queryKey: ['governance', 'movements', workspaceId, period.start, period.end],
    queryFn: () => governanceApi.movements({ workspaceId, periodStart: period.start, periodEnd: period.end }),
    enabled: !!workspaceId,
    staleTime: STALE_TIME_MS,
  })

  const alertsQ = useQuery({
    queryKey: ['governance', 'alerts', workspaceId],
    queryFn: () => governanceApi.alerts(workspaceId),
    enabled: !!workspaceId,
    staleTime: STALE_TIME_MS,
  })

  const envelopes = useMemo(() => ({
    scorecard: scorecardQ.data,
    funnel: funnelQ.data,
    addressability: addressabilityQ.data,
    optants: optantsQ.data,
    movements: movementsQ.data,
    alerts: alertsQ.data,
  }), [scorecardQ.data, funnelQ.data, addressabilityQ.data, optantsQ.data, movementsQ.data, alertsQ.data])

  // Minimum freshness across visible cards — drives the top-level badge.
  const oldestEnvelope = useMemo(() => {
    const all = Object.values(envelopes).filter(Boolean) as Array<{ source_timestamp: string; staleness_warning?: { threshold_seconds: number; actual_age_seconds: number } }>
    if (all.length === 0) return undefined
    return all.reduce((acc, e) =>
      new Date(e.source_timestamp) < new Date(acc.source_timestamp) ? e : acc,
    all[0])
  }, [envelopes])

  if (!isGovernanceEnabled(auth.user)) {
    return (
      <Box sx={{ p: 4 }}>
        <MuiAlert severity="info">
          O Painel de Governança de Benefícios está em implantação. Em breve, sua organização terá acesso.
        </MuiAlert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
          Painel de Governança de Benefícios
        </Typography>
        {oldestEnvelope && <FreshnessBadge envelope={oldestEnvelope} />}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="governance-workspace">Workspace</InputLabel>
            <Select
              labelId="governance-workspace"
              label="Workspace"
              value={workspaceId}
              onChange={e => setWorkspaces([String(e.target.value)])}
              disabled={!workspacesQ.data}
            >
              {workspacesQ.data?.data?.workspaces?.map(w => (
                <MenuItem key={w.workspace_id} value={w.workspace_id}>
                  {w.operadora ? `${w.operadora} — ` : ''}{w.produto ?? w.workspace_id.slice(0, 8)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small" type="date" label="Início do período"
            InputLabelProps={{ shrink: true }}
            value={period.start ?? ''}
            onChange={e => setPeriod({ ...period, start: e.target.value || undefined })}
          />
          <TextField
            size="small" type="date" label="Fim do período"
            InputLabelProps={{ shrink: true }}
            value={period.end ?? ''}
            onChange={e => setPeriod({ ...period, end: e.target.value || undefined })}
          />
        </Stack>
      </Paper>

      {!workspaceId && (
        <MuiAlert severity="info">Selecione um workspace para carregar o painel.</MuiAlert>
      )}

      {workspaceId && (
        <Stack spacing={3}>
          {/* Scorecard */}
          <Section title="Scorecard" envelope={scorecardQ.data} loading={scorecardQ.isLoading} error={scorecardQ.isError}>
            {scorecardQ.data && (
              <Scorecard
                data={scorecardQ.data.data}
                onCardClick={key => {
                  if (key === 'k4') setMovementsDrilldownOpen(true)
                }}
              />
            )}
          </Section>

          {/* Pipeline funnel */}
          <Section title="Funil do pipeline" envelope={funnelQ.data} loading={funnelQ.isLoading} error={funnelQ.isError}>
            {funnelQ.data && <PipelineFunnel data={funnelQ.data.data} />}
          </Section>

          {/* Addressability funnel */}
          <Section title="Endereçabilidade" envelope={addressabilityQ.data} loading={addressabilityQ.isLoading} error={addressabilityQ.isError}>
            {addressabilityQ.data && (
              <AddressabilityFunnel
                data={addressabilityQ.data.data}
                onMaioridadeClick={() => setMaioridadeDrilldownOpen(true)}
                onCargoClick={() => setAddressabilityLossesDrilldownCategory('cargo')}
                onRegiaoLotacaoClick={() => setAddressabilityLossesDrilldownCategory('regiao_lotacao')}
              />
            )}
          </Section>

          {/* Policy / Optants */}
          <Section title="Política e optantes" envelope={optantsQ.data} loading={optantsQ.isLoading} error={optantsQ.isError}>
            {optantsQ.data && (
              <PolicyOptants
                data={optantsQ.data.data}
                onRowClick={code => {
                  if (code === 'K10') setExceptionsDrilldownOpen(true)
                  else if (code === 'K20') setNonOptantsDrilldownOpen(true)
                }}
              />
            )}
          </Section>

          {/* Movements */}
          <Section title="Movimentações" envelope={movementsQ.data} loading={movementsQ.isLoading} error={movementsQ.isError}>
            {movementsQ.data && (
              <Movements
                data={movementsQ.data.data}
                onVigenciasClick={() => setVigenciasDrilldownOpen(true)}
              />
            )}
          </Section>

          {/* Alerts */}
          <Section title="Alertas" envelope={alertsQ.data} loading={alertsQ.isLoading} error={alertsQ.isError}>
            {alertsQ.data && <AlertsList data={alertsQ.data.data} />}
          </Section>

          {/* Drill-down launcher (manual CPF lookup). Integrations with row-level
              click handlers on K19 / K20 tables can be added later. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">Drill-down por CPF</Typography>
              <TextField
                size="small" placeholder="CPF do beneficiário"
                inputRef={cpfInputRef}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    const v = (e.target as HTMLInputElement).value.trim()
                    if (v) setSelectedCpf(v)
                  }
                }}
              />
              <Button variant="outlined" size="small" onClick={() => {
                const v = cpfInputRef.current?.value.trim() ?? ''
                if (v) setSelectedCpf(v)
              }}>Abrir</Button>
            </Stack>
          </Paper>
        </Stack>
      )}

      <CaseDrilldownDialog
        workspaceId={workspaceId}
        beneficiarioTaxPayerId={selectedCpf}
        onClose={() => setSelectedCpf(undefined)}
      />

      <MovementsDrilldownDialog
        workspaceId={workspaceId}
        open={movementsDrilldownOpen}
        onClose={() => setMovementsDrilldownOpen(false)}
      />

      <ExceptionsDrilldownDialog
        workspaceId={workspaceId}
        open={exceptionsDrilldownOpen}
        onClose={() => setExceptionsDrilldownOpen(false)}
      />

      <NonOptantsDrilldownDialog
        workspaceId={workspaceId}
        open={nonOptantsDrilldownOpen}
        onClose={() => setNonOptantsDrilldownOpen(false)}
      />

      <MaioridadeDrilldownDialog
        workspaceId={workspaceId}
        open={maioridadeDrilldownOpen}
        onClose={() => setMaioridadeDrilldownOpen(false)}
      />

      <VigenciasDrilldownDialog
        workspaceId={workspaceId}
        open={vigenciasDrilldownOpen}
        onClose={() => setVigenciasDrilldownOpen(false)}
      />

      <AddressabilityLossesDrilldownDialog
        workspaceId={workspaceId}
        open={addressabilityLossesDrilldownCategory !== null}
        category={addressabilityLossesDrilldownCategory ?? 'cargo'}
        total={
          addressabilityLossesDrilldownCategory === 'regiao_lotacao'
            ? addressabilityQ.data?.data.losses.regiao_lotacao
            : addressabilityQ.data?.data.losses.cargo
        }
        onClose={() => setAddressabilityLossesDrilldownCategory(null)}
      />
    </Box>
  )
}

interface ISectionProps {
  title: string
  envelope?: { source_timestamp: string; staleness_warning?: { threshold_seconds: number; actual_age_seconds: number } }
  loading: boolean
  error: boolean
  children?: React.ReactNode
}

function Section({ title, envelope, loading, error, children }: ISectionProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>{title}</Typography>
        {envelope && <FreshnessBadge envelope={envelope} />}
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {loading && <CircularProgress size={20} />}
      {error && <MuiAlert severity="error">Erro ao carregar {title.toLowerCase()}.</MuiAlert>}
      {!loading && !error && children}
    </Paper>
  )
}
